import { GrayImage2 } from './../image-slice-show/share/image';
import { ImageState } from './../image-slice-show/share/model';
import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { noop, BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Vector3, Vector2 } from 'three';
import { map, filter, distinctUntilChanged, tap } from 'rxjs/operators';
import { GrayImage3 } from '../image-slice-show/share/image';
import { Slicer, SliceDirection } from '../image-slice-show/share/slice';
import { Colormaps, Colormap } from '../image-slice-show/share/colormap';
import { GrayImage2WindowFilter } from '../image-slice-show/share/filter';
import { StoreByArray, WithId } from '../share/store';
import { ImageStoreModule } from './image-store.module';
import { dummyGrayImage } from './http-image-loader/image.debug.repository';

export function initialImageTestState(): ImageState {
  return {
    image: new GrayImage3(
      new Vector3(0, 0, 0),
      new Vector3(1, 1, 0),
      new Vector3(1, 1, 0),
      new Float32Array([1]),
    ),
    slicer: new Slicer('z', 0, 1),
    colormap: Colormaps.GRAY,
    filter: new GrayImage2WindowFilter(1.0, 0.5),
    minMax: new Vector2(0, 1),
    id: uuid(),
    rotation: new Vector3(0, 0, 0),
  };
}

export function initialImageTest2State(): ImageState {
  return {
    image: new GrayImage3(
      new Vector3(0, 0, 0),
      new Vector3(1, 2, 3),
      new Vector3(1, 2, 3),
      new Float32Array([1, 2, 3, 4, 5, 6]),
    ),
    slicer: new Slicer('x', 0, 0, 1),
    colormap: Colormaps.HOT,
    filter: new GrayImage2WindowFilter(6, 3),
    minMax: new Vector2(0, 0.1),
    id: uuid(),
    rotation: new Vector3(0, 0, 0),
  };
}

@Injectable({
  providedIn: ImageStoreModule,
})
export class ImageStoreService {
  public imageStateStore = new StoreByArray<WithId, ImageState>();
  private selectedIdsSubject = new BehaviorSubject<string[]>([]);
  public selected$ = combineLatest(
    this.imageStateStore.state$,
    this.selectedIdsSubject,
  ).pipe(
    map(x => {
      const [images, ids] = x;
      return ids
        .map(id => images.find(img => img.id === id))
        .filter(img => img !== undefined);
    }),
  );

  constructor() {
    this.imageStateStore
      .insert(initialImageTest2State())
      .subscribe(noop, err => console.log(err));
    this.imageStateStore
      .insert(initialImageTestState())
      .subscribe(noop, err => console.log(err));
  }

  image$(id: string): Observable<ImageState> {
    return this.imageStateStore.state$.pipe(
      map(imgs => imgs.find(img => img.id === id)),
      filter((x): x is ImageState => x !== undefined),
      distinctUntilChanged(),
    );
  }
  currentImage(id: string) {
    return this.imageStateStore.currentState().find(s => s.id === id);
  }
  addImage(image: GrayImage3): Observable<string> {
    return new Observable<string>(obs => {
      const id = uuid();
      const toAdd = {
        image: image,
        slicer: new Slicer('z', 0, 0, image.meta.shape.z),
        colormap: Colormaps.GRAY,
        filter: new GrayImage2WindowFilter(1.0, 0.5),
        minMax: new Vector2(0, 1),
        id,
        rotation: new Vector3(0, 0, 0),
      };
      this.imageStateStore
        .insert(toAdd)
        .subscribe(noop, err => console.log(err), () => obs.next(id));
    });
  }

  select(image: ImageState) {
    if (this.selectedIdsSubject.value.includes(image.id)) {
      return;
    }
    if (
      this.imageStateStore.currentState().find(img => img.id === image.id) ===
      undefined
    ) {
      return;
    }
    this.selectedIdsSubject.next([...this.selectedIdsSubject.value, image.id]);
  }

  clearAllSelect() {
    this.selectedIdsSubject.next([]);
  }

  updateImage(state: ImageState, image: GrayImage3) {
    this.imageStateStore
      .update({ ...state, image })
      .subscribe(noop, console.error);
  }
  updateColormap(image: ImageState, colormap: Colormap) {
    this.imageStateStore
      .update({ ...image, colormap })
      .subscribe(noop, e => console.log(e));
  }

  updateAllSelectedColormap(colormap: Colormap) {
    this.mapToAllSelected(image => this.updateColormap(image, colormap));
  }

  updateWidthLevelFilter(image: ImageState, width: number, level: number) {
    this.imageStateStore
      .update({ ...image, filter: new GrayImage2WindowFilter(width, level) })
      .subscribe(noop, e => console.log(e));
  }

  addWidthByStep(image: ImageState, add: number) {
    const widthStep = 0.05;
    this.imageStateStore
      .update({
        ...image,
        filter: image.filter.addWidth(add * widthStep),
      })
      .subscribe(noop, e => console.error(e));
  }

  addLevelByStep(image: ImageState, add: number) {
    const levelStep = 0.05;
    this.imageStateStore
      .update({
        ...image,
        filter: image.filter.addLevel(add * levelStep),
      })
      .subscribe(noop, e => console.log(e));
  }
  addWidthLevelFilterByStep(
    image: ImageState,
    addWidth: number,
    addLevel: number,
  ) {
    const widthStep = 0.05;
    const levelStep = 0.05;
    this.imageStateStore
      .update({
        ...image,
        filter: image.filter.addWidthLevel(
          addWidth * widthStep,
          addLevel * levelStep,
        ),
      })
      .subscribe(noop, e => console.log(e));
  }
  updateDirection(image: ImageState, direction: SliceDirection) {
    this.imageStateStore
      .update({
        ...image,
        slicer: image.slicer.updateDirection(direction, image.image),
      })
      .subscribe(noop, e => console.log(e));
  }

  updateIndex(image: ImageState, index: number) {
    this.imageStateStore
      .update({ ...image, slicer: image.slicer.updateIndex(index) })
      .subscribe(noop, e => console.log(e));
  }

  addIndex(image: ImageState, offset: number) {
    this.imageStateStore
      .update({ ...image, slicer: image.slicer.addIndex(offset) })
      .subscribe(noop, e => console.error(e));
  }

  updateMinMax(min: number, max: number) {
    // this.imageStateStore
    //   .update(
    //     ...this.imagesSelected.map((img) => {
    //       return {
    //         ...img,
    //         minMax: updateColormapMinMax(img.minMax, min, max),
    //       };
    //     })
    //   )
    //   .subscribe(noop, (e) => console.log(e));
    console.warn('Min max will be deprecated');
  }

  updateRotation(image: ImageState, EulerianAngle: Vector3) {
    this.imageStateStore
      .update({
        ...image,
        rotation: EulerianAngle,
      })
      .subscribe(noop, e => console.error(e));
  }
  addRotation(image: ImageState, offset: Vector3) {
    this.imageStateStore
      .update({
        ...image,
        rotation: new Vector3(
          image.rotation.x + offset.x,
          image.rotation.y + offset.y,
          image.rotation.z + offset.z,
        ),
      })
      .subscribe(noop, e => console.error(e));
  }
  mapToAllSelected(op: (image: ImageState) => void) {
    this.selectedIdsSubject.value.forEach(id => {
      const images = this.imageStateStore.currentState();
      const image = images.find(x => x.id === id);
      if (image === undefined) {
        return;
      } else {
        return op(image);
      }
    });
  }

  mapById<T>(id: string, op: (image: ImageState) => T) {
    const current = this.imageStateStore
      .currentState()
      .find(image => image.id === id);
    if (current === undefined) {
      return;
    }
    return op(current);
  }
}
